/*
 * Licensed under the Apache License, Version 2.0 (the "License"); you may not use
 * this file except in compliance with the License. You may obtain a copy of the
 * License at
 *
 *  https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed
 * under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
 * CONDITIONS OF ANY KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations under the License.
 */
import React, { createContext, useContext, useMemo, useRef } from "react";

type ForceCloseFn = () => void;

interface InfoPopoverGroupValue {
  register: (cb: ForceCloseFn) => () => void;
  closeOthers: (self: ForceCloseFn) => void;
}

const InfoPopoverGroupContext = createContext<InfoPopoverGroupValue | null>(
  null
);

/**
 * Provides single-open behaviour to all `InfoPopoverLauncher`s rendered
 * underneath. When any launcher opens, every other launcher inside the
 * same provider closes. Launchers outside the provider — or in a sibling
 * provider — are unaffected.
 *
 * Mount a separate provider per "region" (e.g. legend, dashboard widgets)
 * if you want regions to coordinate independently. If no provider is
 * present, launchers default to a shared fallback group at module scope.
 */
export function InfoPopoverGroupProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const callbacksRef = useRef<Set<ForceCloseFn>>(new Set());

  const value = useMemo<InfoPopoverGroupValue>(
    () => ({
      register: (cb) => {
        callbacksRef.current.add(cb);
        return () => {
          callbacksRef.current.delete(cb);
        };
      },
      closeOthers: (self) => {
        callbacksRef.current.forEach((cb) => {
          if (cb !== self) cb();
        });
      },
    }),
    []
  );

  return (
    <InfoPopoverGroupContext.Provider value={value}>
      {children}
    </InfoPopoverGroupContext.Provider>
  );
}

// Fallback group used when no provider wraps the launcher. Keeps the
// historical app-wide single-open behaviour for callers that haven't
// opted into a scoped provider.
const fallbackCallbacks = new Set<ForceCloseFn>();
const fallbackGroup: InfoPopoverGroupValue = {
  register: (cb) => {
    fallbackCallbacks.add(cb);
    return () => {
      fallbackCallbacks.delete(cb);
    };
  },
  closeOthers: (self) => {
    fallbackCallbacks.forEach((cb) => {
      if (cb !== self) cb();
    });
  },
};

export function useInfoPopoverGroup(): InfoPopoverGroupValue {
  return useContext(InfoPopoverGroupContext) ?? fallbackGroup;
}
